import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { Download, Loader2, RefreshCw, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LIMIT = 20;

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function AckBadge({ acknowledged }) {
  if (acknowledged) {
    return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Acknowledged</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Pending</Badge>;
}

function RowStatusBadge({ recipients }) {
  if (!recipients || recipients.length === 0) return null;
  const allAcked = recipients.every((r) => r.acknowledged);
  if (allAcked) {
    return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs whitespace-nowrap">All acknowledged</Badge>;
  }
  const anyAcked = recipients.some((r) => r.acknowledged);
  if (anyAcked) {
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs whitespace-nowrap">Pending acknowledgements</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs whitespace-nowrap">Pending acknowledgements</Badge>;
}

function SendReminderCell({ delivery, onSent, cardMode = false }) {
  const pendingRecipients = (delivery.recipients || []).filter((r) => !r.acknowledged);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const { token } = useAuth();

  if (pendingRecipients.length === 0) return null;

  async function sendReminders() {
    setSending(true);
    setConfirming(false);
    const skipped = [];
    const sent = [];
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    for (const r of pendingRecipients) {
      try {
        await axios.post(
          `${API_URL}/api/landlord/document-delivery/${encodeURIComponent(delivery.reference)}/resend-ack/${encodeURIComponent(r.email)}`,
          {},
          { withCredentials: true, headers },
        );
        sent.push(r);
      } catch (err) {
        if (err?.response?.status === 429) {
          skipped.push(r);
        } else {
          toast.error(err?.response?.data?.detail || `Failed to send reminder to ${r.email}`);
        }
      }
    }
    setSending(false);
    if (sent.length > 0 && skipped.length === 0) {
      toast.success(`Reminder sent to ${sent.length} recipient(s).`);
    } else if (sent.length > 0 && skipped.length > 0) {
      toast.success(`Reminder sent to ${sent.length} recipient(s). ${skipped.length} recipient(s) were skipped — a reminder was already sent to them recently.`);
    } else if (skipped.length > 0) {
      toast.warning('A reminder was already sent recently — you can send another one tomorrow.');
    }
    if (sent.length > 0) onSent?.();
  }

  if (confirming) {
    if (cardMode) {
      return (
        <div className="space-y-2 w-full">
          <p className="text-xs text-muted-foreground">Send reminder to {pendingRecipients.length} pending recipient(s)?</p>
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 h-10" onClick={sendReminders} disabled={sending}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
            </Button>
            <Button size="sm" variant="ghost" className="flex-1 h-10" onClick={() => setConfirming(false)}>Cancel</Button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-xs text-muted-foreground">Send to {pendingRecipients.length} pending?</span>
        <Button size="sm" className="h-7 px-2 text-xs" onClick={sendReminders} disabled={sending}>
          {sending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm'}
        </Button>
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setConfirming(false)}>Cancel</Button>
      </div>
    );
  }

  if (cardMode) {
    return (
      <Button
        variant="outline"
        className="w-full h-11 gap-2"
        onClick={() => setConfirming(true)}
        disabled={sending}
      >
        <Send className="h-4 w-4" />
        Send Reminder
      </Button>
    );
  }

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 px-2 text-xs gap-1"
      title="Send acknowledgement reminder"
      onClick={() => setConfirming(true)}
      disabled={sending}
    >
      <Send className="h-3 w-3" />
      Remind
    </Button>
  );
}

function DeliveryRow({ delivery, onRefresh }) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadReceipt() {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/landlord/document-delivery/logs/${encodeURIComponent(delivery.reference)}/receipt.pdf`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `delivery-record-${delivery.reference}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download receipt');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <tr className="border-b last:border-0 hover:bg-muted/30 transition-colors">
      <td className="py-3 px-3 text-sm text-navy-800 whitespace-nowrap">{formatDate(delivery.sent_at)}</td>
      <td className="py-3 px-3 text-sm max-w-[160px]">
        <span className="block truncate" title={delivery.document_type}>{delivery.document_type}</span>
      </td>
      <td className="py-3 px-3 text-sm max-w-[180px]">
        <span className="block truncate" title={delivery.property_address}>{delivery.property_address}</span>
      </td>
      <td className="py-3 px-3 text-sm">
        <div className="space-y-1">
          {(delivery.recipients || []).map((r) => (
            <div key={r.email} className="flex items-center gap-1.5 flex-wrap">
              <span className="text-xs text-navy-800">{r.name}</span>
              <AckBadge acknowledged={r.acknowledged} />
            </div>
          ))}
        </div>
      </td>
      <td className="py-3 px-3">
        <RowStatusBadge recipients={delivery.recipients} />
      </td>
      <td className="py-3 px-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs gap-1"
            title="Download delivery receipt"
            onClick={handleDownloadReceipt}
            disabled={downloading}
          >
            {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Receipt
          </Button>
          <SendReminderCell delivery={delivery} onSent={onRefresh} />
        </div>
      </td>
    </tr>
  );
}

function DeliveryCard({ delivery, onRefresh }) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownloadReceipt() {
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/landlord/document-delivery/logs/${encodeURIComponent(delivery.reference)}/receipt.pdf`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `delivery-record-${delivery.reference}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download receipt');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="border-b last:border-0 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-navy-800 leading-snug">{delivery.document_type}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{formatDate(delivery.sent_at)}</p>
        </div>
        <div className="shrink-0">
          <RowStatusBadge recipients={delivery.recipients} />
        </div>
      </div>

      {delivery.property_address && (
        <p className="text-xs text-muted-foreground truncate">{delivery.property_address}</p>
      )}

      <div className="space-y-1.5">
        {(delivery.recipients || []).map((r) => (
          <div key={r.email} className="flex items-center gap-2">
            <span className="text-sm text-navy-800">{r.name}</span>
            <AckBadge acknowledged={r.acknowledged} />
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2 pt-1">
        <Button
          variant="outline"
          className="w-full h-11 gap-2"
          onClick={handleDownloadReceipt}
          disabled={downloading}
        >
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download Receipt
        </Button>
        <SendReminderCell delivery={delivery} onSent={onRefresh} cardMode />
      </div>
    </div>
  );
}

export function LandlordDocumentDeliveryTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchLogs = useCallback(async (targetPage = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/landlord/document-delivery/logs`, {
        withCredentials: true,
        params: { page: targetPage, limit: LIMIT },
      });
      setLogs(res.data.results || []);
      setTotal(res.data.total || 0);
      setPage(targetPage);
    } catch {
      setError('Could not load your delivery history. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(1);
  }, [fetchLogs]);

  if (loading && logs.length === 0) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center px-4">
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={() => fetchLogs(page)}>
          <RefreshCw className="h-4 w-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  if (!loading && logs.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground px-4">
        No document deliveries yet. Use the{' '}
        <Link to="/send-documents" className="text-royal-700 underline">Send Documents tool</Link>{' '}
        to send your first delivery.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Mobile: card list — hidden on sm+ */}
      <Card className="sm:hidden">
        <CardContent className="p-0 divide-y">
          {logs.map((delivery) => (
            <DeliveryCard
              key={delivery.reference}
              delivery={delivery}
              onRefresh={() => fetchLogs(page)}
            />
          ))}
        </CardContent>
      </Card>

      {/* Desktop: table — hidden below sm */}
      <Card className="hidden sm:block">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="py-2.5 px-3 text-xs font-semibold text-muted-foreground whitespace-nowrap">Date sent</th>
                <th className="py-2.5 px-3 text-xs font-semibold text-muted-foreground">Document</th>
                <th className="py-2.5 px-3 text-xs font-semibold text-muted-foreground">Property</th>
                <th className="py-2.5 px-3 text-xs font-semibold text-muted-foreground">Recipients</th>
                <th className="py-2.5 px-3 text-xs font-semibold text-muted-foreground">Status</th>
                <th className="py-2.5 px-3 text-xs font-semibold text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((delivery) => (
                <DeliveryRow
                  key={delivery.reference}
                  delivery={delivery}
                  onRefresh={() => fetchLogs(page)}
                />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {total > LIMIT && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{total} total</span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="h-11 px-4 sm:h-8 sm:px-3 sm:text-sm"
              disabled={page <= 1}
              onClick={() => fetchLogs(page - 1)}
            >
              Previous
            </Button>
            <span className="px-2">Page {page}</span>
            <Button
              variant="outline"
              className="h-11 px-4 sm:h-8 sm:px-3 sm:text-sm"
              disabled={page * LIMIT >= total}
              onClick={() => fetchLogs(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
